import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { normalizeLatexToDoubleDollar } from "../utils/fileHelpers";
import { toast } from "react-toastify";

/**
 * Custom hook that manages WebSocket connection for lesson generation.
 * Includes reconnect, message handling, manual close, state updates,
 * and on-demand reconnection via ensureSocketConnected().
 */
export const useLessonWebSocket = ({
  room_id,
  wsURL,
  course,
  lesson,
  teacher_id,
  scrollChatContainerToBottom,
  showScrollButtonRef,
  setChatHist,
  setShowScrollButton,
  setShowCloseBTN,
  setGenerateAllMode,
  setStopGenerateAllMode,
  generateAllMode,
  setGanrateLesson,
  setIsGenerating,
  setSelectedFiles,
  setuploadedFileLink,
  token,
  setSocketDisconnected,
}) => {
  const [socket, setSocket] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isLessonSocketReady, setIsLessonSocketReady] = useState(false);

  // Refs for internal state management
  const combinedMessageRef = useRef([]);
  const firstMessageRef = useRef(false);
  const reconnectIntervalRef = useRef(1000); // 1s initial
  const reconnectDecay = 1.5;
  const maxReconnectInterval = 30000;
  const maxReconnectAttempts = 5;

  // Refs for on-demand reconnection (ensureSocketConnected)
  const socketRef = useRef(null); // always points to the latest socket instance
  const connectWebSocketRef = useRef(null); // latest connect function (has current room_id/token in closure)
  const pendingOpenResolversRef = useRef([]); // callbacks waiting for the next onopen

  const pendingTextRef = useRef("");
  const pendingUuidRef = useRef(null);
  const flushTimerRef = useRef(null);

  const flushChat = () => {
    flushTimerRef.current = null;
    const text = pendingTextRef.current;
    const uuid = pendingUuidRef.current;
    setChatHist((prev) => {
      const updated = Array.isArray(prev) ? [...prev] : [];
      if (updated.length > 0) {
        // new object, not mutation — lets React.memo work below
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          answer: text,
          uuid,
        };
      }
      return updated;
    });
  };

  // --------------------------
  // 🧩 HANDLE INCOMING MESSAGE
  // --------------------------
  const handleMessage = (event) => {
    // console.log('event', event);
    try {
      const data = JSON.parse(event.data);
      // console.log('data', data);

      if (!data?.message) return;

      if (data?.type === "disconnect") {
        status = true;
        combinedMessageRef.current = [];
        setShowCloseBTN(false);
        setGanrateLesson(false);

        toast.error(
          "Your current input was cleared because the account was logged out from another device.",
          {
            position: "top-center",
          },
        );

        setSocketDisconnected(true);
        return;
      }

      const message = data.message;

      // scrollChatContainerToBottom?.();
      if (!showScrollButtonRef?.current) {
        scrollChatContainerToBottom?.();
      }
      // Push message to combined buffer
      combinedMessageRef.current.push(message);

      let chatdatares = combinedMessageRef.current.join("");
      let status = false;

      // Clean <STARTOFTURN> / <ENDOFTURN> tags and format math
      if (
        chatdatares.includes("<STARTOFTURN>") ||
        chatdatares.includes("<ENDOFTURN>")
      ) {
        chatdatares = normalizeLatexToDoubleDollar(
          chatdatares.replace(/<STARTOFTURN>/g, "").replace(/<ENDOFTURN>/g, ""),
        );

        if (message === "<STARTOFTURN>") {
          firstMessageRef.current = true;
        }

        if (firstMessageRef.current && message !== "<STARTOFTURN>") {
          setShowCloseBTN(true);
          firstMessageRef.current = false;
        }

        // When end of message turn detected
        if (combinedMessageRef.current.join("").includes("<ENDOFTURN>")) {
          status = true;
          combinedMessageRef.current = [chatdatares];
          setShowCloseBTN(false);
          setGanrateLesson(false);
        }
      }

      const uuid = Number(data.uuid);

      pendingTextRef.current = chatdatares;
      pendingUuidRef.current = uuid;

      if (status) {
        // end of turn — flush immediately, cancel any pending timer
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushChat();
        combinedMessageRef.current = [];
      } else if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flushChat, 120);
      }

      // Lesson generation complete
      if (message.includes("<ENDOFLESSON>")) {
        setIsGenerating(false);
      }

      // Cleanup temporary files — bail out when already empty (same ref = no re-render)
      setSelectedFiles((prev) => (prev.length === 0 ? prev : []));
      setuploadedFileLink((prev) => (prev.length === 0 ? prev : []));
    } catch (err) {
      // console.error("❌ Error parsing WebSocket message:", err);
    }
  };

  // --------------------------
  // 🔁 HANDLE RECONNECT LOGIC
  // --------------------------
  const handleReconnect = (connectWebSocket) => {
    // Don't reconnect while the page is hidden/frozen (bfcache). Chrome evicts
    // sockets when a page enters the Back/Forward Cache; retrying against a
    // frozen page just produces a reconnect storm. We resume on
    // 'visibilitychange' -> visible instead (see main effect below).
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      return;
    }

    if (reconnectAttempts < maxReconnectAttempts) {
      setTimeout(() => {
        // Skip the scheduled reconnect if a live socket already exists
        // (e.g. ensureSocketConnected already reconnected in the meantime) —
        // prevents two parallel connections streaming duplicate chunks.
        if (
          socketRef.current &&
          socketRef.current.readyState === WebSocket.OPEN
        ) {
          return;
        }

        setReconnectAttempts((prev) => prev + 1);
        reconnectIntervalRef.current = Math.min(
          reconnectIntervalRef.current * reconnectDecay,
          maxReconnectInterval,
        );
        // console.log(`🔄 Reconnecting... Attempt ${reconnectAttempts + 1}`);
        connectWebSocket();
      }, reconnectIntervalRef.current);
    } else {
      // console.error("🚫 Maximum reconnection attempts reached. Cannot reconnect.");
      setIsLessonSocketReady(true);
    }
  };

  // --------------------------
  // ❌ MANUAL CLOSE FUNCTION
  // --------------------------
  const closeConnection = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.close();
      // console.log("🛑 WebSocket connection closed manually.");
      setShowCloseBTN(false);
      setGanrateLesson(false);
      setStopGenerateAllMode(true);
      setGenerateAllMode(false);
    }
  };

  // ----------------------------------------
  // 🔌 ENSURE CONNECTION (on-demand reconnect)
  // ----------------------------------------
  /**
   * Resolves with an OPEN socket. If the current socket is already open,
   * resolves immediately. If it's CONNECTING, waits for onopen. If it's
   * CLOSED/CLOSING/missing, opens a fresh connection to the same room and
   * resolves once it's ready. Rejects after 10s so callers can show an
   * error instead of hanging forever.
   */
  const ensureSocketConnected = () => {
    return new Promise((resolve, reject) => {
      const current = socketRef.current;

      // Already connected — nothing to do
      if (current && current.readyState === WebSocket.OPEN) {
        resolve(current);
        return;
      }

      if (!connectWebSocketRef.current) {
        reject(new Error("Socket not initialised — missing room_id"));
        return;
      }

      const onOpen = (ws) => {
        clearTimeout(timeoutId);
        resolve(ws);
      };

      const timeoutId = setTimeout(() => {
        // Remove our resolver so a late onopen doesn't call it after rejection
        pendingOpenResolversRef.current =
          pendingOpenResolversRef.current.filter((fn) => fn !== onOpen);
        reject(new Error("WebSocket connection timed out"));
      }, 10000);

      pendingOpenResolversRef.current.push(onOpen);

      // CONNECTING → just wait for onopen to fire.
      // CLOSED / CLOSING / null → start a fresh connection.
      if (
        !current ||
        current.readyState === WebSocket.CLOSED ||
        current.readyState === WebSocket.CLOSING
      ) {
        connectWebSocketRef.current();
      }
    });
  };

  // --------------------------
  // ⚙️ MAIN WEBSOCKET EFFECT
  // --------------------------
  useEffect(() => {
    if (!room_id) {
      setIsLessonSocketReady(false);
      return;
    }

    setIsLessonSocketReady(false);

    let ws;

    const connectWebSocket = () => {
      const wsUrl = `${wsURL}ws/lesson/${room_id}/?course_id=${course?.course_id}&teacher_id=${teacher_id}&lesson_id=${lesson?.lesson_id}&token=${token}`;
      ws = new WebSocket(wsUrl);
      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = () => {
        // console.log("✅ WebSocket connected");
        setIsLessonSocketReady(true);
        setReconnectAttempts(0);
        reconnectIntervalRef.current = 1000;
        combinedMessageRef.current = [];

        // Resolve anyone waiting on ensureSocketConnected()
        const resolvers = pendingOpenResolversRef.current;
        pendingOpenResolversRef.current = [];
        resolvers.forEach((fn) => {
          try {
            fn(ws);
          } catch (err) {
            // console.error("pending open resolver failed:", err);
          }
        });
      };

      ws.onmessage = handleMessage;
      ws.onclose = () => handleReconnect(connectWebSocket);
      ws.onerror = (err) => {
        // console.error("❌ WebSocket error:", err);
        ws.close();
      };
    };

    // Expose the connect function so ensureSocketConnected can reuse it
    connectWebSocketRef.current = connectWebSocket;

    connectWebSocket();

    // ----------------------------------------
    // 🧊 BFCACHE / VISIBILITY HANDLING
    // ----------------------------------------
    // Close the socket cleanly right before the page is frozen so Chrome can
    // bfcache it without logging a "failed: Page entered Back-Forward Cache"
    // eviction, and so handleReconnect never fires against a frozen page.
    // 'pagehide' fires on real navigation away (not on a plain tab switch),
    // which is exactly the bfcache-eligible case.
    const handlePageHide = () => {
      const current = socketRef.current;
      if (current && current.readyState === WebSocket.OPEN) {
        current.close();
      }
    };

    // When the page is restored from bfcache (or re-focused) and the socket is
    // gone, reopen it. onopen resets reconnectAttempts, so this also clears any
    // backoff accrued while hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const current = socketRef.current;
      if (
        !current ||
        current.readyState === WebSocket.CLOSED ||
        current.readyState === WebSocket.CLOSING
      ) {
        connectWebSocket();
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      connectWebSocketRef.current = null;
      socketRef.current = null;
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [room_id]);

  return {
    socket,
    closeConnection,
    combinedMessageRef,
    isLessonSocketReady,
    ensureSocketConnected,
  };
};
